import React, { useMemo, useState } from 'react';
import SoundManager from '../utils/SoundManager';

// A real library: shelves filled with spine books, the four authored books
// shown face-out (cover visible) and clickable to read. Ported from the
// gb4k.fr V2 bookshelf.

interface RealBook { id: string; title: string; cover: string; tag: string; blurb: string; }

const REAL: RealBook[] = [
  { id: 'rotary', title: 'Rotary Disaster', cover: '/books/rotary-disaster.webp', tag: 'Humanitarian · FR/EN',
    blurb: 'A practical disaster-response manual for Rotary clubs, districts and regional stratums. Built from real field operations: preparation, the 0–72 hour response, stabilization, recovery, lessons learned. A usable playbook, not a theory book.' },
  { id: 'evangile', title: 'Évangile Éternel', cover: '/books/evangile-eternel.webp', tag: 'Contemplative theology · FR',
    blurb: 'A meditation on what the Holy Spirit unveils through the completed Revelation. Twenty chapters on the three ages of the soul — Law, Grace, Love — drawn from the Church Fathers and the mystics. A book of contemplative prayer, read slowly.' },
  { id: 'harcelement', title: 'Harcèlement Moral', cover: '/books/harcelement-moral.webp', tag: 'Workplace · Legal · FR',
    blurb: 'A field manual for executives facing workplace bullying — as victim, witness or accused. A four-stage method across 24 chapters: the first-72-hours protocol, a 30-question self-audit, the TRACE evidence method, and 25 ready-to-use tools. Calibrated to French Labor Code 2026.' },
  { id: 'levelup', title: 'Level Up', cover: '/books/level-up.webp', tag: 'Youth · Growth · FR/DE/JP',
    blurb: 'A personal-development manual for teens and young adults, framed entirely in gaming metaphors. 38 chapters on discipline, energy, confidence, focus, relationships, money and mindset — each ending with a concrete challenge. Direct tone, no lecture.' },
];

const SPINE_COLORS = ['#7c1d1d', '#1e2f5a', '#8a6a1e', '#1e5a34', '#3a1e5a', '#5a1e40', '#22484f', '#4a3410', '#2b2b33', '#5a3a1e', '#123a2e', '#402a5a'];
const TITLES = ['STRATEGY', 'THE LONG GAME', 'FIELD NOTES', 'RESILIENCE', 'DEEP WORK', 'THE SUMMIT', 'CROSSINGS', 'SIGNAL', 'THE ASCENT', 'GROUNDWORK', 'AFTERMATH', 'THRESHOLD', 'THE CIRCUIT', 'MERIDIAN', 'DRIFT', 'THE LEDGER', 'HORIZON', 'FRACTURE', 'THE PROTOCOL', 'NORTHBOUND', 'THE ARCHIVE', 'EMBERS', 'WATCHTOWER', 'THE PACT', 'RELAY', 'SANCTUARY', 'THE FORGE', 'MONSOON', 'CIPHER', 'THE VOW', 'LANTERN', 'THE ROUTE', 'BEDROCK', 'HALLOW', 'THE BRIEF', 'KINDLING', 'OUTPOST', 'THE MARGIN', 'TESSERA', 'THE VIGIL'];
const AUTHORS = ['G. BOURGOGNE', 'A. MOREAU', 'K. NAKAMURA', 'R. HADDAD', 'S. OKONKWO', 'L. FERRARI', 'M. DUBOIS'];

function rng(seed: number) { return () => { seed = (seed + 0x6d2b79f5) | 0; let t = Math.imul(seed ^ (seed >>> 15), 1 | seed); t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t; return ((t ^ (t >>> 14)) >>> 0) / 4294967296; }; }

interface Spine { w: number; h: number; color: string; title: string; author: string; tall: boolean; }
function makeSpines(n: number, seed: number): Spine[] {
  const r = rng(seed);
  return Array.from({ length: n }, () => ({
    w: 20 + Math.floor(r() * 20),
    h: 150 + Math.floor(r() * 62),
    color: SPINE_COLORS[Math.floor(r() * SPINE_COLORS.length)],
    title: TITLES[Math.floor(r() * TITLES.length)],
    author: AUTHORS[Math.floor(r() * AUTHORS.length)],
    tall: r() > 0.5,
  }));
}

const BookshelfVisualization: React.FC = () => {
  const [reading, setReading] = useState<RealBook | null>(null);
  const shelves = useMemo(() => [
    { spines: makeSpines(13, 1), real: [REAL[0]] },
    { spines: makeSpines(15, 2), real: [REAL[1], REAL[2]] },
    { spines: makeSpines(14, 3), real: [REAL[3]] },
  ], []);

  const play = (s: string) => { try { SoundManager.playSound(s); } catch { /* ignore */ } };

  if (reading) {
    return (
      <div className="lib-body">
        <div className="reader">
          <button className="reader-back" onClick={() => { play('toggle'); setReading(null); }}>◂ BACK TO SHELF</button>
          <div className="reader-spread">
            <img className="reader-cover" src={reading.cover} alt={`${reading.title} cover`} />
            <div className="reader-text">
              <h3>{reading.title}</h3>
              <p className="book-tag">{reading.tag}</p>
              <p>{reading.blurb}</p>
              <p className="reader-note">Full text coming soon — chapters will be readable here, page by page.</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="lib-body">
      <p className="shelf-hint">THE GB4K LIBRARY — click a highlighted cover to read it</p>
      <div className="lib">
        {shelves.map((sh, si) => {
          const items: React.ReactNode[] = [];
          const insertAt = sh.real.map((_, i) => Math.floor(((i + 1) * sh.spines.length) / (sh.real.length + 1)));
          let realIdx = 0;
          sh.spines.forEach((sp, i) => {
            if (realIdx < sh.real.length && i === insertAt[realIdx]) {
              const rb = sh.real[realIdx++];
              items.push(
                <button key={`r${rb.id}`} className="face-book" onClick={() => { play('command'); setReading(rb); }} aria-label={rb.title}>
                  <img src={rb.cover} alt={rb.title} loading="lazy" />
                </button>,
              );
            }
            items.push(
              <div key={`s${si}-${i}`} className="spine" style={{ width: sp.w, height: sp.h, background: `linear-gradient(90deg, rgba(0,0,0,.4), ${sp.color} 30%, ${sp.color} 70%, rgba(0,0,0,.45))` }}>
                <span className="spine-title" style={{ fontSize: sp.w > 30 ? 11 : 9 }}>{sp.title}</span>
                {sp.tall && <span className="spine-author">{sp.author}</span>}
              </div>,
            );
          });
          return <div key={si} className="lib-shelf"><div className="lib-row">{items}</div><div className="lib-plank" /></div>;
        })}
      </div>
    </div>
  );
};

export default BookshelfVisualization;
